import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { Gauge, Banknote, Shield, TrendingUp, AlertTriangle, RefreshCcw, Activity } from 'lucide-react';

const CURRENCIES = ['GBP', 'INR', 'CAD', 'USD'];

const fmt = (n, cur) => {
  const symbols = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$' };
  return `${symbols[cur] || ''}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const KpiCard = ({ title, value, cur, sub, tone = 'neutral', testid }) => {
  const borderClass = tone === 'danger' ? 'border-red-400' : tone === 'good' ? 'border-emerald-500' : 'border-[#E07A3A]';
  return (
    <div data-testid={testid} className={`bg-white rounded-xl p-6 border-l-4 ${borderClass} shadow-sm`}>
      <div className="text-xs uppercase tracking-widest text-[#1F3D2B] mb-2">{title}</div>
      <div className="text-3xl font-semibold text-[#1A1A1A]">{fmt(value, cur)}</div>
      {sub && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

export const AdminSovereignDashboard = () => {
  const [currency, setCurrency] = useState('GBP');
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [ghost, setGhost] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sovereign/readiness`, { params: { days, currency } }),
        axios.get(`${API_URL}/api/admin/sovereign/ghost-status`),
      ]);
      setReport(r.data);
      setGhost(g.data);
    } catch (e) {
      toast.error('Failed to load Sovereign Dashboard');
    } finally {
      setLoading(false);
    }
  }, [days, currency]);

  useEffect(() => { load(); }, [load]);

  if (loading && !report) {
    return <div className="py-20 text-center text-slate-500" data-testid="sovereign-loading">Loading Sovereign Dashboard…</div>;
  }
  if (!report) return null;

  const r = report.revenue;
  const goalHit = report.goal_hit;

  return (
    <div className="space-y-6" data-testid="admin-sovereign-dashboard">
      {/* Header + toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E07A3A]">
            <Shield className="h-3.5 w-3.5" /> Ghost-Mode · Zero-Manual Infrastructure
          </div>
          <h2 className="text-2xl font-semibold text-[#1A1A1A] font-['Playfair_Display']">Sovereign Revenue Dashboard</h2>
          <p className="text-sm text-slate-500 mt-1">Consolidated into <strong>{report.currency}</strong> · last {report.period_days} days · generated {new Date(report.generated_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <select data-testid="sovereign-currency-select" value={currency} onChange={(e) => setCurrency(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5">
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="sovereign-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1.5">
            {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button data-testid="sovereign-refresh-btn" onClick={load} className="flex items-center gap-1 bg-[#1F3D2B] text-white rounded px-3 py-1.5 hover:bg-[#2E5340]">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-testid="sovereign-send-digest-btn"
            onClick={async () => {
              try {
                const res = await axios.post(`${API_URL}/api/admin/sovereign/digest/send-now`);
                if (res.data?.sent) toast.success(`Digest sent to ${res.data.to}`);
                else toast.error(`Digest skipped: ${res.data?.skipped || res.data?.error || 'unknown'}`);
              } catch {
                toast.error('Digest send failed');
              }
            }}
            className="flex items-center gap-1 bg-[#E07A3A] text-white rounded px-3 py-1.5 hover:bg-[#CC6A2E]"
          >
            Email Weekly Digest
          </button>
        </div>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-unlock-fees" title="Unlock Fees" value={r.unlock_fees.total} cur={report.currency} sub={`${r.unlock_fees.count} paid unlocks`} />
        <KpiCard testid="kpi-partner-subs" title="Partner Subs" value={r.package_subs.total} cur={report.currency} sub={`${r.package_subs.count} transactions`} />
        <KpiCard testid="kpi-pulse-subs" title="Pulse Engine" value={r.pulse_subs.total} cur={report.currency} sub={`${r.pulse_subs.count} buyer subs + boosts`} />
        <KpiCard testid="kpi-ad-earnings" title="Accrued Ad Earnings" value={r.ad_earnings.total} cur={report.currency} sub={`${r.ad_earnings.page_views.toLocaleString()} page views · $${r.ad_earnings.rpm_usd} RPM`} />
      </div>

      {/* Profit / Margin summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-[#FCF4E9]">
              <Gauge className="h-6 w-6 text-[#E07A3A]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#1F3D2B]">Gross Revenue</div>
              <div className="text-3xl font-bold text-[#1A1A1A]">{fmt(r.gross_total, report.currency)}</div>
            </div>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between gap-6"><span className="text-slate-500">Auto expenses</span><span className="font-medium">{fmt(report.expenses.total, report.currency)}</span></div>
            <div className="flex justify-between gap-6"><span className="text-slate-500">Net profit</span><span data-testid="sovereign-profit" className={`font-semibold ${report.profit < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmt(report.profit, report.currency)}</span></div>
            <div className="flex justify-between gap-6"><span className="text-slate-500">Margin</span>
              <span data-testid="sovereign-margin" className={`font-semibold ${goalHit ? 'text-emerald-600' : 'text-[#E07A3A]'}`}>{report.margin_pct}% / goal {report.margin_goal_pct}%</span>
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div data-testid="sovereign-margin-bar" className={`h-full ${goalHit ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`} style={{ width: `${Math.min(100, Math.max(0, report.margin_pct))}%` }} />
        </div>
        {!goalHit && (
          <div className="mt-3 text-xs text-[#E07A3A] flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Below 70% profit-margin goal — favour SEO traffic over paid ads to close the gap.
          </div>
        )}
      </div>

      {/* Stripe + expense breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Banknote className="h-4 w-4 text-[#1F3D2B]" />
            <h3 className="font-semibold">Stripe Live Balance</h3>
          </div>
          {report.stripe_balance.error ? (
            <div className="text-sm text-red-500">Error: {report.stripe_balance.error}</div>
          ) : (
            <div className="text-sm space-y-1" data-testid="sovereign-stripe-balance">
              <div className="flex justify-between"><span className="text-slate-500">Available</span><span className="font-semibold">{fmt(report.stripe_balance.available, report.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Pending</span><span>{fmt(report.stripe_balance.pending, report.currency)}</span></div>
              <div className="text-xs text-slate-400 mt-2">Live from Stripe · consolidated to {report.currency}</div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[#1F3D2B]" />
            <h3 className="font-semibold">Auto-Computed Expenses</h3>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Variable (${report.expenses.cost_per_txn_usd}/txn × {report.transactions_count})</span><span>${report.expenses.variable_usd}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Fixed infra (pro-rated from ${report.expenses.monthly_infra_usd}/mo)</span><span>${report.expenses.infra_usd}</span></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="text-slate-700 font-medium">Total</span><span className="font-semibold">{fmt(report.expenses.total, report.currency)}</span></div>
          </div>
          <div className="text-xs text-slate-400 mt-3">{report.expenses.note}</div>
        </div>
      </div>

      {/* Ghost-mode flags */}
      {ghost && (
        <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="sovereign-ghost-status">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-[#1F3D2B]" />
            <h3 className="font-semibold">Ghost-Mode Safety Flags</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {[
              ['Inline Monetizer locked', ghost.inline_monetizer_locked],
              ['PII scrub active', ghost.pii_scrub_active],
              ['No Twilio lookups', !ghost.twilio_lookup_used],
              ['No manual AdSense', !ghost.adsense_manual_entry],
              ['Stripe Balance synced', ghost.stripe_balance_sync],
              ['Match Guarantee on', ghost.match_guarantee_enabled],
            ].map(([label, ok]) => (
              <div key={label} className={`flex items-center gap-2 rounded px-3 py-2 border ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span className="text-xs">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSovereignDashboard;
